import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import { UITabs, UIPaginationBar, UIButton } from "../../ui";
import * as companyPortalEventsApi from "../../../network/apis/companyPortalEvents";
import { getCompanyPortalCities } from "../../../network/apis/companyPortalEventRequests";
import { normalizeRoleLookupOptions } from "../../Events/Event/Applicants/eventRequestMappers";
import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  companyPermissionModules,
  eventStatuses,
  eventTypes,
  lookupsKeys,
} from "../../../utils/Constants";
import { canAddCompanyModule } from "../../../utils/Permissions";
import { textContainOnlySpaces, showErrorMsg } from "../../../utils/Helpers";
import { getPortalApiErrorMessage } from "../../../utils/companyPortal/eventApi";
import {
  COMPANY_EVENT_DRAFT_TAB,
  COMPANY_EVENT_PENDING_TAB,
  getCompanyEventListStatusParam,
  getCompanyEventListTab,
} from "../../../utils/companyPortal/eventListTabs";
import {
  flattenCompanyPortalEventsList,
  readPortalEventsListMeta,
  extractPortalLookupArray,
  mapEventTypeLookupRowsToFilterOptions,
} from "../../../utils/companyPortal/portalEventsList";

import EventsListFilters from "./EventsListFilters";
import EventsListTable from "./EventsListTable";
import { Box } from "@mui/material";

import "./EventsList.scss";
import { Plus } from "lucide-react";

const formatFilterDate = (date) => dayjs(date).format("YYYY-MM-DD");

const isSameOrAfterDate = (date, minDate) => {
  if (!minDate) return true;
  const rowDate = dayjs(date);
  const filterDate = dayjs(minDate);
  if (!rowDate.isValid() || !filterDate.isValid()) return false;
  return (
    rowDate.isSame(filterDate, "day") || rowDate.isAfter(filterDate, "day")
  );
};

const isSameOrBeforeDate = (date, maxDate) => {
  if (!maxDate) return true;
  const rowDate = dayjs(date);
  const filterDate = dayjs(maxDate);
  if (!rowDate.isValid() || !filterDate.isValid()) return false;
  return (
    rowDate.isSame(filterDate, "day") || rowDate.isBefore(filterDate, "day")
  );
};

const filterEventsListRows = (rows, filters) => {
  const search = filters.search.toLowerCase();
  const status = String(filters.status || "")
    .trim()
    .toLowerCase();

  return rows.filter((row) => {
    if (
      status &&
      String(getCompanyEventListTab(row) || "")
        .trim()
        .toLowerCase() !== status
    ) {
      return false;
    }

    if (filters.paymentPeriod && row.payment_period !== filters.paymentPeriod) {
      return false;
    }

    if (filters.eventTypeId != null && filters.eventTypeId !== "") {
      if (String(row.event_type_id ?? "") !== String(filters.eventTypeId)) {
        return false;
      }
    }

    if (
      filters.startDate &&
      !isSameOrAfterDate(row.event_start_at, filters.startDate)
    ) {
      return false;
    }

    if (
      filters.endDate &&
      !isSameOrBeforeDate(row.event_end_at, filters.endDate)
    ) {
      return false;
    }

    if (!search) return true;

    return (
      String(row.name || "")
        .toLowerCase()
        .includes(search) ||
      String(row.id || "")
        .toLowerCase()
        .includes(search)
    );
  });
};

const CompanyPortalEventsList = () => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const isLoading = useSelector((state) => state.loader);
  const { events, shared } = messages[lang];
  const canAdd = canAddCompanyModule(companyPermissionModules.events, login);

  const recurrenceFilterOptions = useMemo(
    () => [
      { id: 1, name: events.listEvents.daily, value: eventTypes.daily },
      { id: 2, name: events.listEvents.monthly, value: eventTypes.monthly },
    ],
    [events.listEvents.daily, events.listEvents.monthly],
  );

  const eventTabs = [
    {
      value: eventStatuses.ongoing,
      label: events.listEvents.eventTabs.ongoing,
    },
    {
      value: eventStatuses.upcoming,
      label: events.listEvents.eventTabs.upcoming,
    },
    {
      value: eventStatuses.past,
      label: events.listEvents.eventTabs.previous,
    },
    {
      value: COMPANY_EVENT_DRAFT_TAB,
      label: events.listEvents.eventTabs.draft,
    },
    {
      value: COMPANY_EVENT_PENDING_TAB,
      label: events.listEvents.eventTabs.pending,
    },
    {
      value: eventStatuses.cancelled,
      label: events.listEvents.eventTabs.cancelled,
    },
  ];

  const initialTab = (() => {
    const tab = new URLSearchParams(location.search).get("tab");
    const valid = [...Object.values(eventStatuses), COMPANY_EVENT_PENDING_TAB];
    return tab && valid.includes(tab) ? tab : eventStatuses.ongoing;
  })();

  const [filterEventStatus, setFilterEventStatus] = useState(initialTab);
  const [filterRecurrence, setFilterRecurrence] = useState(null);
  const [filterCatalogEventType, setFilterCatalogEventType] = useState(null);
  const [catalogEventTypeOptions, setCatalogEventTypeOptions] = useState([]);
  const [filterCrewSize, setFilterCrewSize] = useState(null);
  const [crewSizeOptions, setCrewSizeOptions] = useState([]);
  const [filterCity, setFilterCity] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);

  const [clearFilters, setClearFilters] = useState(false);

  const endDateBeforeStart =
    !!filterStartDate &&
    !!filterEndDate &&
    dayjs(filterEndDate).isBefore(dayjs(filterStartDate), "day");

  useEffect(() => {
    if (clearFilters) setClearFilters(false);
  }, [clearFilters]);

  useEffect(() => {
    let cancelled = false;

    companyPortalEventsApi
      .getCompanyPortalLookups([lookupsKeys.eventTypes, lookupsKeys.crewSizePlans])
      .then((res) => {
        if (cancelled) return;
        const raw = extractPortalLookupArray(res, lookupsKeys.eventTypes);
        setCatalogEventTypeOptions(mapEventTypeLookupRowsToFilterOptions(raw));
        setCrewSizeOptions(
          extractPortalLookupArray(res, lookupsKeys.crewSizePlans).map((plan) => ({
            id: plan.id,
            name: plan.name,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogEventTypeOptions([]);
          setCrewSizeOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
    // Refetch on a language change: event type and crew size names come back in the request locale.
  }, [lang]);

  useEffect(() => {
    let cancelled = false;

    getCompanyPortalCities({ page: -1 })
      .then((response) => {
        if (cancelled) return;
        setCityOptions(normalizeRoleLookupOptions(response.data?.data, lang));
      })
      .catch(() => {
        if (!cancelled) setCityOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    pages: 1,
    count: 0,
  });

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch((searchValue || "").trim()),
      400,
    );
    return () => clearTimeout(t);
  }, [searchValue]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    const valid = [...Object.values(eventStatuses), COMPANY_EVENT_PENDING_TAB];
    const nextStatus =
      tab && valid.includes(tab) ? tab : eventStatuses.ongoing;
    setFilterEventStatus(nextStatus);
  }, [location.search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filterEndDate,
    filterEventStatus,
    filterRecurrence,
    filterCatalogEventType,
    filterCrewSize,
    filterCity,
    filterStartDate,
    pageSize,
  ]);

  useEffect(() => {
    let cancelled = false;

    const trimmed = debouncedSearch;
    if (trimmed.length > 0 && textContainOnlySpaces(trimmed)) {
      return undefined;
    }
    if (endDateBeforeStart) {
      return undefined;
    }

    const params = {
      page,
      items: pageSize,
      status: getCompanyEventListStatusParam(filterEventStatus),
    };

    if (trimmed.length > 0) {
      params.query = trimmed;
    }

    if (filterRecurrence?.value) {
      params.payment_period = filterRecurrence.value;
    }

    if (
      filterCatalogEventType?.id != null &&
      filterCatalogEventType.id !== ""
    ) {
      params.event_type_id = filterCatalogEventType.id;
    }

    if (filterCrewSize?.id != null && filterCrewSize.id !== "") {
      params.crew_size_plan_id = filterCrewSize.id;
    }

    if (filterCity?.id != null && filterCity.id !== "") {
      params.city_id = filterCity.id;
    }

    if (filterStartDate) {
      params.event_start_date = formatFilterDate(filterStartDate);
    }

    if (filterEndDate) {
      params.event_end_date = formatFilterDate(filterEndDate);
    }

    companyPortalEventsApi
      .getCompanyPortalEventsList(params)
      .then((response) => {
        if (cancelled) return;
        const apiRows = flattenCompanyPortalEventsList(response);
        let nextMeta = readPortalEventsListMeta(response);

        if (
          nextMeta.count === 0 &&
          apiRows.length > 0 &&
          nextMeta.pages === 1
        ) {
          const approx = (page - 1) * pageSize + apiRows.length;
          nextMeta = {
            ...nextMeta,
            count: Math.max(apiRows.length, approx),
          };
        }

        setList(apiRows);
        setMeta(nextMeta);

        const serverPage = Number.isFinite(+nextMeta.currentPage)
          ? +nextMeta.currentPage
          : page;
        const pages = Number.isFinite(+nextMeta.pages) ? +nextMeta.pages : 1;

        if (pages > 0 && serverPage > pages) {
          setPage(pages);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setList([]);
        showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
      });

    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    debouncedSearch,
    filterEndDate,
    filterEventStatus,
    filterRecurrence,
    filterCatalogEventType,
    filterCrewSize,
    filterCity,
    filterStartDate,
    endDateBeforeStart,
    lang,
    shared.generalErrMsg,
  ]);

  const handleSelectTab = (status) => {
    setFilterEventStatus(status);
    setSearchValue("");
    setDebouncedSearch("");
    setFilterRecurrence(null);
    setFilterCatalogEventType(null);
    setFilterCity(null);
    setFilterCrewSize(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setClearFilters(true);
    setPage(1);

    const params = new URLSearchParams(location.search);
    params.set("tab", status);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const visibleRows = useMemo(
    () =>
      filterEventsListRows(list, {
        search: debouncedSearch,
        status: filterEventStatus,
        paymentPeriod: filterRecurrence?.value || "",
        eventTypeId: filterCatalogEventType?.id,
        startDate: endDateBeforeStart ? null : filterStartDate,
        endDate: endDateBeforeStart ? null : filterEndDate,
      }),
    [
      debouncedSearch,
      endDateBeforeStart,
      filterCatalogEventType,
      filterEndDate,
      filterEventStatus,
      filterRecurrence,
      filterStartDate,
      list,
    ],
  );

  // The picked option is shown from the refetched list, so its name follows the language too.
  const selectedCatalogEventType =
    catalogEventTypeOptions.find((option) => option.id === filterCatalogEventType?.id) ??
    filterCatalogEventType;
  const selectedCrewSize =
    crewSizeOptions.find((option) => option.id === filterCrewSize?.id) ?? filterCrewSize;

  const hasFilteredRows = visibleRows.length !== list.length;
  const paginationMeta = hasFilteredRows
    ? {
        currentPage: 1,
        pages: 1,
        count: visibleRows.length,
      }
    : meta;

  const isEmptyView = !isLoading && visibleRows.length === 0;

  const from =
    paginationMeta.count === 0
      ? 0
      : (paginationMeta.currentPage - 1) * pageSize + 1;
  const to = Math.min(
    paginationMeta.currentPage * pageSize,
    paginationMeta.count,
  );
  const basicInfoLabels = events.creatEvent.basicInformation;

  const crewSizeLabel = events.creatEvent.crewSize.stepLabel;
  const tableLabels = {
    eventsColumn: events.listEvents.eventsColumn,
    eventTypeColumn: basicInfoLabels.eventType,
    recurrenceColumn: basicInfoLabels.recurrence,
    crewSizeColumn: crewSizeLabel,
    startDate: events.listEvents.startDate,
    endDate: events.listEvents.endDate,
    emptyAction: events.listEvents.emptyAction,
    emptyTitle: events.listEvents.emptyTitle,
    eventIdPrefix: events.listEvents.eventIdPrefix,
    eventCategoryLabels: {
      entertainment: basicInfoLabels.entertainment,
      sports: basicInfoLabels.sports,
      conference: basicInfoLabels.conference,
    },
    recurrency: {
      daily: events.listEvents.daily,
      monthly: events.listEvents.monthly,
    },
  };

  const handleRowClick = (row) => {
    if (!row?.id) return;
    history.push(
      ROUTE_PATHS.companyViewEvent.replace(":id", encodeURIComponent(row.id)),
    );
  };

  const handleCreateEvent = () => {
    history.push(ROUTE_PATHS.companyCreateEvent);
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "var(--space-18)" }}
    >
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "var(--space-10)",
        width: "100%",
      }} className="company-portal-events-list-tabs-row">
        <UITabs
          value={filterEventStatus}
          onChange={handleSelectTab}
          tabs={eventTabs}
          className="company-portal-events-list-tabs"
          aria-label={events.listEvents.listEventsBreadcrumb}
          classNames={{
            root: "w-100"
          }}
        />

        {canAdd && (
          <UIButton
            size="large"
            appearance="gradient"
            type="button"
            startIcon={<Plus />}
            onClick={() => history.push(ROUTE_PATHS.companyCreateEvent)}
          >
            {shared.creatEvent}
          </UIButton>
        )}
      </Box>
      <EventsListFilters
        searchPlaceholder={events.listEvents.searchPlaceHolder}
        searchValue={searchValue}
        onSearchChange={(v) => setSearchValue(v)}
        catalogEventTypePlaceholder={basicInfoLabels.eventType}
        catalogEventTypeValue={selectedCatalogEventType}
        catalogEventTypeOptions={catalogEventTypeOptions}
        onCatalogEventTypeChange={setFilterCatalogEventType}
        recurrencePlaceholder={basicInfoLabels.recurrence}
        recurrenceValue={filterRecurrence}
        recurrenceOptions={recurrenceFilterOptions}
        onRecurrenceChange={setFilterRecurrence}
        cityPlaceholder={events.listEvents.city}
        crewSizePlaceholder={crewSizeLabel}
        crewSizeValue={selectedCrewSize}
        crewSizeOptions={crewSizeOptions}
        onCrewSizeChange={setFilterCrewSize}
        cityValue={filterCity}
        cityOptions={cityOptions}
        onCityChange={setFilterCity}
        startDateLabel={events.listEvents.startDate}
        startDateValue={filterStartDate}
        onStartDateChange={setFilterStartDate}
        endDateLabel={events.listEvents.endDate}
        endDateValue={filterEndDate}
        onEndDateChange={setFilterEndDate}
        clearDates={clearFilters}
        endDateBeforeStart={endDateBeforeStart}
      />
      <div className="d-flex flex-column justify-content-between">
        <EventsListTable
          rows={visibleRows}
          pageSize={pageSize}
          isEmpty={isEmptyView}
          noDataDescription={events.listEvents.noDataDescription}
          labels={tableLabels}
          onRowClick={handleRowClick}
          onCreateEvent={canAdd ? handleCreateEvent : undefined}
        />
        {!isEmptyView && (
          <UIPaginationBar
            currentPage={paginationMeta.currentPage}
            pages={paginationMeta.pages}
            pageSize={pageSize}
            rangeTemplate={events.listEvents.showingEventsRange}
            rangeFrom={from}
            rangeTo={to}
            rangeTotal={paginationMeta.count}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </Box>
  );
};

export default CompanyPortalEventsList;
